import * as amplitude from "@amplitude/analytics-browser";
import { Auth } from "aws-amplify";

const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
const environment = process.env.REACT_APP_ENVIRONMENT || "alpha";
const amplitudeEnabled = process.env.REACT_APP_ENABLE_AMPLITUDE !== "false"; // Default to true unless explicitly set to "false"

// Helper function to track amplitude events
const trackAmplitudeEvent = (category, properties) => {
  if (!amplitudeEnabled || !amplitude || !apiKey) return;
  
  amplitude.track(category, properties);
};

// Debug logging to help troubleshoot feature flag
console.log("[Amplitude] Feature flag status:", {
  amplitudeEnabled: amplitudeEnabled,
  apiKey: apiKey ? "present" : "missing"
});

// Centralized error logging function
const logErrorToAmplitude = (category, eventName, severity, message, details = {}, context = {}) => {
  if (!amplitudeEnabled) {
    return; // Skip logging if amplitude is disabled
  }
  
  const errorProperties = {
    event_category: category,
    event_name: eventName,
    severity: severity,
    message: message,
    details: details,
    context: {
      environment: environment,
      timestamp: Date.now(),
      url: window.location.href,
      user_agent: navigator.userAgent,
      ...context
    }
  };
  
  trackAmplitudeEvent("errors", errorProperties);
};

if (apiKey && amplitudeEnabled) {
  amplitude.init(apiKey, {
    defaultTracking: true,
    configuration: {
      environment,
    },
  });
} else if (!amplitudeEnabled) {
  console.log("[Amplitude] Disabled via feature flag");
} else {
  console.warn("[Amplitude] Missing API Key.");
}

// global error handler for uncaught errors
window.onerror = function (message, source, lineno, colno, error) {
  logErrorToAmplitude(
    "errors",
    "javascript_error",
    "error",
    message,
    {
      source: source,
      line: lineno,
      column: colno,
      stack: error?.stack,
      error_object: error ? JSON.stringify(error, Object.getOwnPropertyNames(error)) : undefined
    }
  );
};

// global handler for unhandled promise rejections
window.onunhandledrejection = function (event) {
  const reason = event.reason;
  const message = reason?.message || String(reason);
  
  logErrorToAmplitude(
    "errors",
    "javascript_error",
    "error",
    message,
    {
      stack: reason?.stack,
      error_object: reason ? JSON.stringify(reason, Object.getOwnPropertyNames(reason)) : undefined
    }
  );
};

// console.error wrapper
const originalConsoleError = window.console.error;
window.console.error = function (...args) {
  originalConsoleError.apply(window.console, args);
  
  const message = args.map(arg => 
    typeof arg === 'string' ? arg : JSON.stringify(arg, Object.getOwnPropertyNames(arg))
  ).join(' | ');
  
  const errorArg = args.find(arg => arg?.stack);
  
  logErrorToAmplitude(
    "errors",
    "javascript_error",
    "error",
    message,
    {
      stack: errorArg?.stack,
      error_objects: args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, Object.getOwnPropertyNames(arg)) : undefined
      ).filter(Boolean)
    }
  );
};

// Patch fetch to log failed network requests
if (window.fetch) {
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    try {
      const response = await originalFetch.apply(this, args);
      if (!response.ok) {
        let responseBody;
        try {
          responseBody = await response.clone().text();
        } catch (e) {
          responseBody = undefined;
        }
        
        logErrorToAmplitude(
          "errors",
          "api_error",
          response.status >= 500 ? "critical" : "error",
          `${response.status} ${response.statusText}`,
          {
            url: response.url,
            status: response.status,
            status_text: response.statusText,
            response_body: responseBody
          },
          {
            request_method: args[1]?.method || "GET"
          }
        );
      }
      return response;
    } catch (error) {
      logErrorToAmplitude(
        "errors",
        "api_error",
        "critical",
        error.message,
        {
          url: args[0],
          stack: error.stack,
          error_object: JSON.stringify(error, Object.getOwnPropertyNames(error))
        }
      );
      throw error;
    }
  };
}

// Helper function to safely parse numeric values from Unity
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  // Handle Unity Single/Double objects
  if (typeof value === 'object' && value !== null) {
    try {
      return parseFloat(value.toString()) || 0;
    } catch (e) {
      console.warn("[Amplitude] Failed to parse numeric value:", value, e);
      return 0;
    }
  }
  return 0;
};

// Helper function to safely track amplitude events
const safeAmplitudeTrack = (eventCategory, eventProperties) => {
  if (!amplitudeEnabled) {
    return; // Skip tracking if amplitude is disabled
  }
  
  trackAmplitudeEvent(eventCategory, eventProperties);
};

// Helper function to safely set amplitude user ID
const safeAmplitudeSetUserId = (userId) => {
  if (!amplitudeEnabled) {
    return; // Skip setting user ID if amplitude is disabled
  }
  
  if (amplitude && apiKey) {
    amplitude.setUserId(userId);
  } else {
    console.warn(`[Amplitude] User ID not set - missing API key: ${userId}`);
  }
};

// React event handler in dm-web
window.dispatchReactUnityEvent = (eventType, ...args) => {
  // Forward Unity events to Amplitude
  if (eventType.startsWith("ReactLog")) {
    handleUnityAmplitudeEvent(eventType, args);
  }
  // Handle other Unity events
  // TODO (nvogler)
  // Do we need this?
  //handleUnityEvent(eventType, args);
};

// Unity-to-React amplitude events
const handleUnityAmplitudeEvent = (eventType, args) => {
  const eventMap = {
    ReactAwake: () => {
      safeAmplitudeTrack("navigation", {
        event_name: "scene_loaded",
        scene: "unity_awake",
        environment: environment,
        source: "unity",
      });
    },

    ReactLogUserId: (userId) => {
      safeAmplitudeSetUserId(userId);
    },

    ReactLogButtonPress: (env, isInternal, buttonName) => {
      safeAmplitudeTrack("navigation", {
        event_name: "feature_accessed",
        feature_name: buttonName,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogLoginTimeViewed: (env, isInternal, memberName, totalSeconds) => {
      const baseProps = {
        view_name: memberName,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      };
      
      safeAmplitudeTrack("navigation", { ...baseProps, event_name: "view_entered" });
    },

    ReactLogAppTimeViewed: (env, isInternal, memberName, accountType, totalSeconds) => {
      const baseProps = {
        view_name: memberName,
        account_type: accountType,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      }; 
      
      safeAmplitudeTrack("navigation", { ...baseProps, event_name: "view_entered" });
    },

    ReactLogAuthEvent: (env, isInternal, eventName, username) => {
      const props = {
        event_name: eventName,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      };

      if (username) {
        props.username = username;
      }

      // Add user data for login events
      if ((eventName === "ProviderLogIn" || eventName === "PatientLogIn" || eventName === "login_success") && window.currentUser?.attributes) {
        const attrs = window.currentUser.attributes;
        props.amplify_user_id = attrs.sub;
        props.email = attrs.email;
        props.user_type = attrs["custom:user_type"];
        props.is_provider = attrs["custom:isprovider"];
        props.registration_date = attrs["custom:registration_date"];
      }

      safeAmplitudeTrack("authentication", props);
    },

    ReactLogSleeveLocationAssignment: (env, isInternal, bgxCode, location) => {
      safeAmplitudeTrack("device_management", {
        event_name: "device_connected",
        bgx_code: bgxCode,
        location,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogROMCompleted: (env, isInternal, protocol, side, maxAngle, isRTM, timeElapsed, sessionId) => {
      safeAmplitudeTrack("activities", {
        event_name: "activity_completed",
        activity_type: "rom",
        protocol,
        side,
        max_angle: maxAngle,
        is_rtm: isRTM === 1 || isRTM === true,
        time_elapsed: timeElapsed,
        session_id: sessionId,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogSleeveConnectionSuccess: (env, isInternal, bgxCode) => {
      safeAmplitudeTrack("device_management", {
        event_name: "device_connected",
        bgx_code: bgxCode,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogSleeveConnectionFailure: (env, isInternal, bgxCode) => {
      safeAmplitudeTrack("device_management", {
        event_name: "device_disconnected",
        bgx_code: bgxCode,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogSleeveWriteError: (env, isInternal, bgxCode, writeType, errorMessage) => {
      safeAmplitudeTrack("errors", {
        event_name: "device_error",
        bgx_code: bgxCode,
        write_type: writeType,
        error_message: errorMessage,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogPerformanceMetrics: (env, isInternal, metricsJson) => {
      let metrics;
      try {
        metrics = JSON.parse(metricsJson);
      } catch (error) {
        console.warn("[Amplitude] Failed to parse performance metrics JSON:", error);
        metrics = { raw_data: metricsJson };
      }

      safeAmplitudeTrack("performance", {
        event_name: "performance_metrics",
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
        metrics: metrics,
      });
    },

    ReactLogViewLoadTime: (sceneName, duration) => {
      safeAmplitudeTrack("performance", {
        event_name: "load_time",
        scene: sceneName,
        load_time_seconds: safeParseFloat(duration),
        source: "unity",
      });
    },

    ReactLogViewExit: (env, isInternal, viewName, timeSpent, exitReason) => {
      safeAmplitudeTrack("navigation", {
        event_name: "view_exited",
        view_name: viewName,
        view_time_seconds: safeParseFloat(timeSpent),
        exit_reason: exitReason,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogOverlayTimeViewed: (env, isInternal, overlayName, totalSeconds) => {
      const baseProps = {
        overlay_name: overlayName,
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      };
      
      safeAmplitudeTrack("performance", { ...baseProps, event_name: "overlay_viewed", view_time_seconds: safeParseFloat(totalSeconds) });
    },

    ReactLogOverlayNavigation: (env, isInternal, overlayName, eventName, timeSpent) => {
      safeAmplitudeTrack("navigation", {
        event_name: eventName,
        overlay_name: overlayName,
        view_time_seconds: safeParseFloat(timeSpent),
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },

    ReactLogViewNavigation: (env, isInternal, viewName, eventName, timeSpent) => {
      safeAmplitudeTrack("navigation", {
        event_name: eventName,
        view_name: viewName,
        view_time_seconds: safeParseFloat(timeSpent),
        environment: env,
        is_internal: isInternal === "1",
        source: "unity",
      });
    },
  };

  const handler = eventMap[eventType];
  if (handler) {
    handler(...args);
  } else {
    console.warn(`[Amplitude] Unhandled Unity event type: ${eventType}`, args);
  }
};
